import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Bell, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError, resolveFileUrl } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = { title: "", body: "", image_url: "", target: "all", send_push: false };

export default function PengumumanAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Sila muat naik fail imej sahaja");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload?is_public=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: res.data.url }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat naik imej");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/announcements", form);
      toast.success(form.send_push ? `Pengumuman diterbitkan • ${res.data.pushed_count} notifikasi push dihantar` : "Pengumuman diterbitkan");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/announcements/${id}`);
    toast.success("Pengumuman dipadam");
    load();
  };

  return (
    <div data-testid="admin-pengumuman-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-piks-black">Pengumuman</h1>
          <p className="text-sm text-piks-muted">Terbitkan pengumuman kepada ahli PPPS.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-announcement-btn" className="bg-piks-black hover:bg-piks-black/90"><Plus className="w-4 h-4 mr-1.5" /> Pengumuman Baharu</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Pengumuman Baharu</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Tajuk</Label><Input required data-testid="announcement-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Kandungan</Label><Textarea required data-testid="announcement-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>Gambar (pilihan)</Label>
                <p className="text-xs text-piks-muted mt-0.5 mb-1.5">Contoh: poster kursus. Akan dipaparkan di laman utama & Berita.</p>
                <input ref={fileInputRef} type="file" accept="image/*" data-testid="announcement-image-input" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                {form.image_url ? (
                  <div className="relative w-full h-40 rounded-lg overflow-hidden border border-piks-border">
                    <img src={resolveFileUrl(form.image_url)} alt="Pratonton" className="w-full h-full object-cover" data-testid="announcement-image-preview" />
                    <button type="button" data-testid="announcement-image-remove-btn" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="absolute top-2 right-2 p-1.5 rounded-full bg-piks-black/70 text-white hover:bg-piks-black"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <button type="button" data-testid="announcement-image-upload-btn" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="w-full h-24 rounded-lg border-2 border-dashed border-piks-border flex flex-col items-center justify-center gap-1.5 text-piks-muted hover:border-piks-gold/50 hover:text-piks-black transition-colors disabled:opacity-60">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
                    <span className="text-xs">{uploading ? "Memuat naik..." : "Klik untuk muat naik gambar"}</span>
                  </button>
                )}
              </div>
              <div>
                <Label>Sasaran</Label>
                <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                  <SelectTrigger data-testid="announcement-target-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Ahli</SelectItem>
                    <SelectItem value="committee">Jawatankuasa</SelectItem>
                    <SelectItem value="member">Ahli</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-piks-ivory border border-piks-border">
                <Checkbox id="send_push" data-testid="announcement-send-push-checkbox" checked={form.send_push} onCheckedChange={(v) => setForm({ ...form, send_push: !!v })} className="mt-0.5" />
                <Label htmlFor="send_push" className="text-sm leading-relaxed text-piks-black flex items-center gap-1.5">
                  <Bell className="w-3.5 h-3.5 shrink-0" /> Hantar juga sebagai notifikasi push kepada ahli yang mengaktifkan notifikasi
                </Label>
              </div>
              <Button type="submit" disabled={saving} data-testid="announcement-submit-btn" className="w-full bg-piks-black hover:bg-piks-black/90">{saving ? "Menerbitkan..." : "Terbitkan"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="p-5 rounded-xl bg-white border border-piks-border flex items-start justify-between gap-4" data-testid={`admin-announcement-${a.id}`}>
            <div className="flex items-start gap-4">
              {a.image_url && (
                <img src={resolveFileUrl(a.image_url)} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0 border border-piks-border" data-testid={`admin-announcement-image-${a.id}`} />
              )}
              <div>
                <p className="font-semibold text-piks-black">{a.title}</p>
                <p className="text-sm text-piks-muted mt-1">{a.body}</p>
                <p className="text-[11px] text-piks-muted mt-2 uppercase tracking-wide">Sasaran: {a.target}</p>
              </div>
            </div>
            <button onClick={() => remove(a.id)} data-testid={`delete-announcement-${a.id}`} className="text-piks-muted hover:text-piks-red shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
